// @ts-check
/**
 * Typed client for superuser API key management (Dev Tools).
 *
 * TYPES ARE GENERATED, NOT HAND-WRITTEN. See `schema.d.ts` header for details.
 */
import axios from 'axios'

/** @typedef {import('./schema').components['schemas']} Schemas */

/** @typedef {Schemas['APIKey']} APIKey */
/** @typedef {Schemas['APIKeyCreateRequest']} APIKeyCreateRequest */
/** @typedef {Schemas['APIKeyCreateResponse']} APIKeyCreateResponse */
/** @typedef {Schemas['APIKeyConnectCheckResponse']} ConnectCheck */
/** @typedef {Schemas['APIKeyConnectRequest']} ConnectApproveRequest */
/** @typedef {Schemas['APIKeyConnectResponse']} ConnectApproveResponse */

/**
 * GET /api/admin/api-keys/ — every API key across tenants, newest first.
 * @returns {Promise<APIKey[]>}
 */
export async function listApiKeys() {
  const { data } = await axios.get('/api/admin/api-keys/')
  return data
}

/**
 * POST /api/admin/api-keys/ — mint a key that acts as the given user.
 * The plaintext `key` is only ever returned by this call.
 * @param {APIKeyCreateRequest} body
 * @returns {Promise<APIKeyCreateResponse>}
 */
export async function createApiKey(body) {
  const { data } = await axios.post('/api/admin/api-keys/', body)
  return data
}

/**
 * POST /api/admin/api-keys/{id}/revoke/ — deactivate a key. Idempotent.
 * @param {string} id
 * @returns {Promise<APIKey>}
 */
export async function revokeApiKey(id) {
  const { data } = await axios.post(`/api/admin/api-keys/${id}/revoke/`)
  return data
}

/**
 * GET /api/api-keys/connect/ — validate the client and its destination before
 * showing the approval page. Issues nothing. The cli client sends `port`, the
 * kailash client sends `redirect_uri`.
 * @param {{client: 'cli' | 'kailash', port?: number, redirect_uri?: string}} params
 * @returns {Promise<ConnectCheck>}
 */
export async function checkConnect(params) {
  const { data } = await axios.get('/api/api-keys/connect/', { params })
  return data
}

/**
 * POST /api/api-keys/connect/ — approve a connect request; returns the
 * one-time authorization code to hand back to the redirect_uri.
 * @param {ConnectApproveRequest} body
 * @returns {Promise<ConnectApproveResponse>}
 */
export async function approveConnect(body) {
  const { data } = await axios.post('/api/api-keys/connect/', body)
  return data
}
